/**
 * Tipos TypeScript globales de Zen
 * Espejo de los modelos Django para type-safety en el frontend
 */

// ─── Paginación ───────────────────────────────────────────────────────────────
export interface PaginatedResponse<T> {
  pagination: {
    count: number
    total_pages: number
    current_page: number
    page_size: number
    next: string | null
    previous: string | null
  }
  results: T[]
}

// ─── Autenticación ────────────────────────────────────────────────────────────
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  access: string
  refresh?: string  // Now sent as httpOnly cookie, not in response body
  user: {
    id: string
    email: string
    full_name: string
    is_platform_admin: boolean
  }
  tenants: TenantSummary[]
}

export interface TenantSummary {
  schema: string
  name: string
  roles: RoleType[]
  permissions: Record<string, string[]>
  disabled_modules?: string[]
  logo_url?: string | null
  domain?: string | null
  country?: string | null
}

// ─── Tenants ──────────────────────────────────────────────────────────────────
export type TenantStatus = 'active' | 'inactive' | 'suspended' | 'provisioning'
export type CountryCode = 'CL' | 'PE' | 'AR' | 'MX' | 'EC' | 'OT'

export type AccountType = 'production' | 'trial' | 'demo'

export interface Tenant {
  id: number
  schema_name: string
  name: string
  tax_id: string
  country: CountryCode
  country_display: string
  status: TenantStatus
  status_display: string
  account_type: AccountType
  account_type_display: string
  licensed_users: number
  max_users: number
  allow_homologation: boolean
  active_users: number
  monthly_cost_usd: number | null
  created_at: string
  trial_ends_at: string | null
}

// ─── Usuarios y Roles ─────────────────────────────────────────────────────────
export type RoleType =
  | 'admin'
  | 'accountant'
  | 'seller'
  | 'collector'
  | 'buyer'
  | 'treasurer'
  | 'manager'
  | 'viewer'
  | 'hr_manager'
  | 'store_manager'
  | 'cashier'

export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  phone: string
  avatar_url: string | null
  email_verified: boolean
  is_platform_admin: boolean
  ui_preferences?: UiPreferences
  last_seen_at: string | null
  created_at: string
}

/** Preferencias de interfaz por usuario (persisten en el backend, no en el browser). */
export interface UiPreferences {
  /** Vista predeterminada del centro de la Vista 360 de ventas. */
  sales_360_default_view?: 'detail' | 'pdf' | 'xml'
  [key: string]: unknown
}

export interface Role {
  id: number
  name: string
  role_type: RoleType
  role_type_display?: string
  description: string
  permissions: Record<string, string[]>
  is_system_role?: boolean
  eligible_for_commissions?: boolean
  /** Lo que puede hacer en el terminal del POS (catálogo de `modules/pos/capabilities`). */
  pos_capabilities?: string[]
  pos_max_discount_pct?: string
}

export interface TenantMembership {
  tenant_schema: string
  tenant_name: string
  roles: { type: RoleType; name: string }[]
  status: 'pending' | 'accepted' | 'revoked'
  is_active: boolean
  accepted_at: string | null
}

// ─── Shared / Utilities ───────────────────────────────────────────────────────
export type SortOrder = 'asc' | 'desc'

export interface SelectOption<T = string> {
  value: T
  label: string
}

export interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

// Monedas soportadas por país
export const COUNTRY_CURRENCIES: Record<CountryCode, { code: string; symbol: string; locale: string }> = {
  CL: { code: 'CLP', symbol: '$', locale: 'es-CL' },
  PE: { code: 'PEN', symbol: 'S/', locale: 'es-PE' },
  AR: { code: 'ARS', symbol: '$', locale: 'es-AR' },
  MX: { code: 'MXN', symbol: '$', locale: 'es-MX' },
  EC: { code: 'USD', symbol: '$', locale: 'es-EC' },
  OT: { code: 'USD', symbol: '$', locale: 'en-US' },
}

// Nombres de países
export const COUNTRY_NAMES: Record<CountryCode, string> = {
  CL: 'Chile',
  PE: 'Perú',
  AR: 'Argentina',
  MX: 'México',
  EC: 'Ecuador',
  OT: 'Otro',
}

// Nombres de roles
export const ROLE_NAMES: Record<RoleType, string> = {
  admin: 'Administrador',
  accountant: 'Contador',
  seller: 'Vendedor',
  collector: 'Cobrador',
  buyer: 'Comprador',
  treasurer: 'Tesorero',
  manager: 'Gerente',
  viewer: 'Solo Lectura',
  hr_manager: 'Gerente de RRHH',
  store_manager: 'Encargado de local',
  cashier: 'Cajero',
}
